import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Inbox, Calendar } from "lucide-react";
import { getApplicationTransparency } from "@/lib/api";

const STATUS_STYLES = {
  applied:      { label: "Applied",      cls: "bg-stone-100 text-stone-600" },
  shortlisted:  { label: "Shortlisted",  cls: "bg-blue-100 text-blue-700" },
  interviewing: { label: "Interviewing", cls: "bg-amber-100 text-amber-700" },
  offered:      { label: "Offered",      cls: "bg-emerald-100 text-emerald-700" },
  rejected:     { label: "Rejected",     cls: "bg-red-100 text-red-600" },
};

export default function ApplicationTransparencyPage() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getApplicationTransparency();
        const raw = Array.isArray(data?.applications) ? data.applications : (Array.isArray(data) ? data : []);
        // Backend returns candidate_rank_pct (% you beat), your_score, avg_score, company_name.
        // Map to the shape AppCard renders. percentile = "top X%" = 100 - rank_pct.
        const normalized = raw.map((a, i) => {
          const yourScore = a.your_score ?? a.user_score ?? 0;
          const avgScore = a.avg_score ?? yourScore;
          const rankPct = a.candidate_rank_pct ?? null;
          const percentile = a.percentile ?? (rankPct != null ? Math.max(1, Math.round(100 - rankPct)) : 50);
          return {
            id: a.application_id ?? a.id ?? String(i),
            job_title: a.job_title ?? "Role",
            company: a.company_name ?? a.company ?? "—",
            status: a.status ?? "applied",
            applied_at: a.applied_at ?? a.created_at ?? null,
            percentile,
            user_score: Math.round(yourScore),
            avg_score: Math.round(avgScore),
            // backend doesn't return a top score — estimate it above the user/avg so the bar renders
            top_score: Math.round(a.top_score ?? Math.min(100, Math.max(yourScore, avgScore) + 6)),
            total_applicants: a.total_applicants ?? 0,
          };
        });
        setApps(normalized);
      } catch {
        setApps([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="container-x py-10 md:py-14">
      <div className="mb-10">
        <span className="overline">Transparency</span>
        <h1 className="font-display text-4xl md:text-5xl mt-2">Your application pipeline</h1>
        <p className="text-stone-500 mt-2 max-w-lg leading-relaxed">
          See exactly where you stand among all applicants for every role you&apos;ve applied to.
        </p>
      </div>

      {loading ? (
        <div className="space-y-5">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-3xl border border-stone-200 bg-white p-8 animate-pulse space-y-4">
              <div className="h-5 w-48 bg-stone-200 rounded-full" />
              <div className="h-10 w-32 bg-stone-200 rounded-full" />
              <div className="h-4 w-full bg-stone-200 rounded-full" />
            </div>
          ))}
        </div>
      ) : apps.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="space-y-5">
          {apps.map((app, i) => (
            <AppCard key={app.id} app={app} delay={i * 0.1} />
          ))}
        </div>
      )}
    </div>
  );
}

function AppCard({ app, delay }) {
  const st = STATUS_STYLES[app.status] ?? STATUS_STYLES.applied;
  const pct = app.percentile;
  const tier = pct <= 10 ? "green" : pct <= 25 ? "amber" : "stone";
  const tierText =
    pct <= 10
      ? { color: "text-emerald-600", bg: "bg-emerald-50 border-emerald-200" }
      : pct <= 25
      ? { color: "text-amber-600", bg: "bg-amber-50 border-amber-200" }
      : { color: "text-stone-600", bg: "bg-stone-50 border-stone-200" };

  // Bar positioning
  const range = app.top_score - 50; // assume min visible = 50
  const normalize = (v) => Math.max(0, Math.min(100, ((v - 50) / range) * 100));
  const userPos = normalize(app.user_score);
  const avgPos = normalize(app.avg_score);
  const topPos = normalize(app.top_score);

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45, delay }}
      className={`rounded-3xl border p-8 ${tierText.bg}`}
    >
      {/* Header row */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
        <div>
          <p className="text-xs font-mono text-stone-400 uppercase tracking-widest mb-1">{app.company}</p>
          <h3 className="font-display text-2xl">{app.job_title}</h3>
          <div className="flex items-center gap-3 mt-2">
            <span className={`text-xs px-3 py-1 rounded-full font-medium ${st.cls}`}>{st.label}</span>
            <span className="flex items-center gap-1 text-xs text-stone-400">
              <Calendar size={11} />
              {new Date(app.applied_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
            </span>
          </div>
        </div>

        {/* Percentile display */}
        <div className="shrink-0 text-right">
          <p className={`font-display text-5xl font-bold ${tierText.color}`}>Top {pct}%</p>
          <p className="text-xs text-stone-400 mt-1">of {app.total_applicants} candidates</p>
        </div>
      </div>

      {/* Score comparison bar */}
      <div>
        <p className="text-xs font-mono text-stone-500 uppercase tracking-widest mb-3">Score comparison</p>
        <div className="relative h-3 rounded-full bg-stone-200 overflow-visible mb-3">
          {/* Filled bar up to user score */}
          <motion.div
            className="absolute left-0 top-0 h-full rounded-full"
            style={{ background: tier === "green" ? "#15803D" : tier === "amber" ? "#D97706" : "#78716C" }}
            initial={{ width: 0 }}
            whileInView={{ width: `${userPos}%` }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
          />
          {/* Dot markers only — values live in the legend below (no floating labels = no overlap) */}
          <ScoreMarker pos={avgPos} color="#94A3B8" />
          <ScoreMarker pos={topPos} color="#1C1917" />
          <ScoreMarker pos={userPos} color={tier === "green" ? "#15803D" : tier === "amber" ? "#D97706" : "#78716C"} highlight />
        </div>
        {/* Legend */}
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs font-mono mt-4">
          <span className="inline-flex items-center gap-1.5"><Dot c={tier === "green" ? "#15803D" : tier === "amber" ? "#D97706" : "#78716C"} /> You <strong className="text-stone-800">{app.user_score}</strong></span>
          <span className="inline-flex items-center gap-1.5"><Dot c="#94A3B8" /> Avg <strong className="text-stone-700">{app.avg_score}</strong></span>
          <span className="inline-flex items-center gap-1.5"><Dot c="#1C1917" /> Top <strong className="text-stone-700">{app.top_score}</strong></span>
        </div>
      </div>
    </motion.div>
  );
}

function Dot({ c }) {
  return <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: c }} />;
}

function ScoreMarker({ pos, color, highlight = false }) {
  return (
    <div
      className="absolute top-1/2 w-3.5 h-3.5 rounded-full border-2 border-white"
      style={{ left: `${pos}%`, transform: "translate(-50%, -50%)", background: color, boxShadow: highlight ? `0 0 0 3px ${color}55` : "none" }}
    />
  );
}

function EmptyState() {
  return (
    <div className="rounded-3xl border-2 border-dashed border-stone-200 bg-stone-50 p-16 flex flex-col items-center text-center gap-4">
      <div className="p-4 rounded-2xl bg-stone-200 text-stone-500"><Inbox size={28} /></div>
      <h3 className="font-display text-2xl">No applications yet</h3>
      <p className="text-stone-500 max-w-sm leading-relaxed">
        Apply to jobs to see your real-time standing among applicants.
      </p>
    </div>
  );
}
